import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import { CHAIN_IDS } from "../constants";
import { getApiBase } from "../../../lib/remote-config";

export const TOKEN_INFO_QUERY_KEY = "wp:token-info";

interface TokenInfo {
  id: number;
  chain_id: number;
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  image: string;
}

interface TokenInfoResponse {
  code: number;
  message: string;
  data: TokenInfo[];
}

export function useTokenInfo(env: Environment, enabled = true) {
  const crossChainId = CHAIN_IDS[env].cross;

  const { data, ...rest } = useQuery<TokenInfoResponse>({
    queryKey: [TOKEN_INFO_QUERY_KEY, env],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("walletServer", env);
      const res = await fetch(`${baseUrl}/v1/public/token/info`, {
        cache: "no-store",
        signal,
      });
      if (!res.ok) throw new Error(`Failed to fetch token info: ${res.status}`);
      const json: TokenInfoResponse = await res.json();
      if (json.code !== 200) throw new Error(`Token info API error: ${json.code}`);
      return json;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const { byChainAddr, byChainSym } = useMemo(() => {
    const addrMap = new Map<string, TokenInfo>();
    const symMap = new Map<string, TokenInfo>();
    if (!data?.data) return { byChainAddr: addrMap, byChainSym: symMap };

    for (const t of data.data) {
      const addr = t.address.toLowerCase();
      addrMap.set(`${t.chain_id}:${addr}`, t);
      addrMap.set(addr, t);

      const sym = t.symbol.replace(/^t/, "").toUpperCase();
      const chainSymKey = `${t.chain_id}:${sym}`;
      if (!symMap.has(chainSymKey)) symMap.set(chainSymKey, t);
      if (!symMap.has(sym)) symMap.set(sym, t);
    }

    return { byChainAddr: addrMap, byChainSym: symMap };
  }, [data]);

  const getTokenImage = (address: string, symbol?: string): string => {
    const addr = address.toLowerCase();

    const byChain = byChainAddr.get(`${crossChainId}:${addr}`);
    if (byChain?.image) return byChain.image;

    const byAddr = byChainAddr.get(addr);
    if (byAddr?.image) return byAddr.image;

    if (symbol) {
      const sym = symbol.replace(/^t/, "").toUpperCase();
      const byChainSymbol = byChainSym.get(`${crossChainId}:${sym}`);
      if (byChainSymbol?.image) return byChainSymbol.image;

      const bySym = byChainSym.get(sym);
      if (bySym?.image) return bySym.image;
    }

    return "";
  };

  return { getTokenImage, ...rest };
}
